import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';
import { PessoaFotoDialogComponent } from '@/shared/components/pessoa/pessoa-foto-dialog/pessoa-foto-dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { ConfirmationService } from 'primeng/api';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { EstruturaService } from '@/core/api/sicom-api/sistema/estrutura/estrutura.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { EstruturaResponse } from '@/core/api/sicom-api/sistema/estrutura/estrutura.model';
import { EstruturaDetalhe } from '../dialogs/estrutura-detalhe/estrutura-detalhe';
import { LayoutService } from '@/layout/service/layout.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-estrutura-lista',
    standalone: true,
    imports: [
        FormsModule,
        ButtonModule,
        TableModule,
        TagModule,
        TooltipModule,
        PessoaAvatarComponent,
        PessoaFotoDialogComponent,
        InputTextModule,
        SelectModule,
        ConfirmDialogModule,
        PaginatorModule,
        TableViewSwitcherComponent,
        CardContent,
        CardContainer,
        EstruturaDetalhe,
        CanDirective,
        FiltrosBar,
        FiltroCampo
    ],
    providers: [ConfirmationService],
    templateUrl: './estrutura-organizacional-lista.html',
    styleUrl: './estrutura-organizacional-lista.scss'
})
/** Lista/árvore de estruturas organizacionais e seus coordenadores. */
export class EstruturaOrganizacionalLista implements OnInit {
    protected readonly roleEstruturasCriar = PERMISSOES.configuracoes.estruturas.criar;
    protected readonly roleEstruturasEditar = PERMISSOES.configuracoes.estruturas.editar;
    protected readonly roleEstruturasAlterarStatus = PERMISSOES.configuracoes.estruturas.alterarStatus;

    private readonly service = inject(EstruturaService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly notificacoes = inject(NotificacoesService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly layoutService = inject(LayoutService);

    protected readonly currentView = signal<'card' | 'table'>(this.layoutService.getPresentationType());
    protected readonly carregando = signal(false);

    // ── Filtros ────────────────────────────────────────────────────────────
    /**
     * Rascunho do que está digitado nos campos. O `computed` de baixo lê os sinais **aplicados**,
     * não estes: filtro client-side sem essa separação filtraria a cada tecla e o botão "Filtrar"
     * viraria enfeite.
     */
    protected filtroTexto = signal('');
    protected filtroStatus = signal<string>('');

    /** Valores efetivamente em vigor na lista — só mudam no "Filtrar"/"Limpar". */
    private readonly filtroTextoAplicado = signal('');
    private readonly filtroStatusAplicado = signal('');

    protected readonly opcoesStatus = [
        { label: 'Todos', value: '' },
        { label: 'Ativos', value: 'A' },
        { label: 'Inativos', value: 'I' }
    ];

    // ── Dados ──────────────────────────────────────────────────────────────
    protected readonly _todas = signal<EstruturaResponse[]>([]);

    protected readonly dadosFiltrados = computed(() => {
        const texto = this.filtroTextoAplicado().toLowerCase().trim();
        const status = this.filtroStatusAplicado();
        return this._todas().filter((e) => {
            const matchTexto = !texto || e.descricao?.toLowerCase().includes(texto) || e.sigla?.toLowerCase().includes(texto) || e.coordenador?.toLowerCase().includes(texto) || String(e.id).includes(texto);
            const matchStatus = !status || e.inStatus === status;
            return matchTexto && matchStatus;
        });
    });

    // ── Estatísticas ───────────────────────────────────────────────────────
    protected readonly totalAtivos = computed(() => this._todas().filter((e) => e.inStatus === 'A').length);
    protected readonly totalInativos = computed(() => this._todas().filter((e) => e.inStatus !== 'A').length);

    // ── Paginação compartilhada ────────────────────────────────────────────
    protected paginaAtual = signal(0);
    protected itensPorPagina = signal(12);

    protected readonly estruturasPaginadas = computed(() => {
        const inicio = this.paginaAtual() * this.itensPorPagina();
        return this.dadosFiltrados().slice(inicio, inicio + this.itensPorPagina());
    });

    protected get primeiroItem(): number {
        return this.paginaAtual() * this.itensPorPagina();
    }

    // ── Dialogs ────────────────────────────────────────────────────────────
    protected modalDetalhe = signal(false);
    protected estruturaSelecionada = signal<EstruturaResponse | null>(null);

    private readonly recarregar$ = new Subject<void>();

    constructor() {
        this.recarregar$
            .pipe(
                switchMap(() => {
                    this.carregando.set(true);
                    return this.service.listar();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (dados) => {
                    const mapa = new Map(dados.map((d) => [d.id, d.descricao]));
                    this._todas.set(dados.map((d) => ({ ...d, nomeSuperior: mapa.get(d.idEoSuperior ?? -1) })));
                    this.carregando.set(false);
                },
                error: () => {
                    this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao carregar estruturas organizacionais' });
                    this.carregando.set(false);
                }
            });
    }

    ngOnInit(): void {
        this.carregar();
    }

    protected carregar(): void {
        this.recarregar$.next();
    }

    protected novaEstrutura(): void {
        this.estruturaSelecionada.set(null);
        this.modalDetalhe.set(true);
    }

    protected editarEstrutura(e: EstruturaResponse): void {
        this.estruturaSelecionada.set(e);
        this.modalDetalhe.set(true);
    }

    protected confirmarToggleStatus(e: EstruturaResponse): void {
        const acao = e.inStatus === 'A' ? 'inativar' : 'ativar';
        this.confirmationService.confirm({
            message: `Deseja ${acao} a estrutura "${e.descricao}"?`,
            header: 'Confirmação',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            accept: () => this.toggleStatus(e)
        });
    }

    private toggleStatus(e: EstruturaResponse): void {
        this.service
            .toggleStatus(e.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (atualizado) => {
                    const label = atualizado.inStatus === 'A' ? 'ativada' : 'inativada';
                    this.notificacoes.notificar({ severity: 'success', summary: `Estrutura ${label} com sucesso.` });
                    this.carregar();
                },
                error: () => this.notificacoes.notificar({ toast: false, severity: 'error', summary: 'Erro ao alterar status' })
            });
    }

    protected onSalvo(): void {
        this.modalDetalhe.set(false);
        this.carregar();
    }

    protected onPaginacaoMudar(event: PaginatorState): void {
        this.paginaAtual.set(event.page ?? 0);
        this.itensPorPagina.set(event.rows ?? 12);
    }

    /** Único gatilho do filtro: o que está no rascunho passa a valer para a lista. */
    protected aplicarFiltros(): void {
        this.filtroTextoAplicado.set(this.filtroTexto().trim());
        this.filtroStatusAplicado.set(this.filtroStatus());
        this.paginaAtual.set(0);
    }

    protected limparFiltros(): void {
        this.filtroTexto.set('');
        this.filtroStatus.set('');
        this.aplicarFiltros();
    }

    protected statusSeverity(inStatus: string): 'success' | 'danger' {
        return inStatus === 'A' ? 'success' : 'danger';
    }

    protected statusLabel(inStatus: string): string {
        return inStatus === 'A' ? 'Ativo' : 'Inativo';
    }

    protected iniciaisCoordenador(e: EstruturaResponse): string {
        const fonte = e.coordenador || e.descricao || '';
        const partes = fonte.trim().split(/\s+/).filter(Boolean);
        if (partes.length === 0) return 'EO';
        return partes.length === 1 ? partes[0][0].toUpperCase() : `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
    }

    /** Foto do coordenador em exibição no diálogo de ampliação; `null` o mantém fechado. */
    protected readonly fotoAmpliada = signal<string | null>(null);
    protected readonly nomeFotoAmpliada = signal('');

    /** Amplia a foto que o avatar emitiu, anunciando de qual coordenador ela é. */
    protected ampliarFoto(url: string, estrutura: EstruturaResponse): void {
        this.nomeFotoAmpliada.set(estrutura.coordenador || '');
        this.fotoAmpliada.set(url);
    }

    protected get temFiltrosAtivos(): boolean {
        return !!this.filtroTextoAplicado() || !!this.filtroStatusAplicado();
    }
}
